import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { formatTime } from '@trellistry/platform/i18n';
import { Actions, AuthLayout, Button, CheckField, Notice, TextField } from '@trellistry/ui';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { beginPasskeySignIn, beginWithPassword, completePasskeySignIn, completeSecondStep, type BeginResult, type Session } from '../api.js';
import { signInFailure } from './sign-in-messages.js';

/**
 * Staff sign-in (design §5): a passkey on its own, or a password and then the code from the authenticator app or a
 * backup code. A remembered device skips the second step for 30 days. A first sign-in goes on to setup. The messages
 * use church-friendly words and never say which of the address or the password was wrong.
 */
export function SignIn({ onSignedIn, onSetupRequired, notice: firstNotice }: { onSignedIn: (s: Session) => void; onSetupRequired: (grantId: string) => void; notice?: string | null }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(false);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [methods, setMethods] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ text: string; error?: boolean } | null>(firstNotice ? { text: firstNotice } : null);

  const handle = (r: BeginResult) => {
    if ('error' in r) { setNotice({ text: 'Something went wrong on our side. Please try again.', error: true }); return; }
    switch (r.status) {
      case 'signed_in': onSignedIn(r.session); return;
      case 'second_step': setChallengeId(r.challengeId); setMethods(r.methods); setNotice(null); return;
      case 'setup_required': onSetupRequired(r.grantId); return;
      case 'failed': setNotice({ text: challengeId ? 'That code didn’t match. Check the time on your phone and try again.' : 'That email address and password don’t match our records.', error: true }); return;
      case 'locked': setNotice({ text: `Too many attempts. Please wait until ${formatTime(new Date(r.until))} and try again.`, error: true }); return;
      case 'expired': setNotice({ text: 'That took a little too long. Please start again.', error: true }); setChallengeId(null); setCode(''); return;
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      handle(challengeId ? await completeSecondStep(challengeId, code, remember) : await beginWithPassword(email, password));
    } catch (e) {
      setNotice({ text: signInFailure(e), error: true });
    } finally {
      setBusy(false);
    }
  };

  const withPasskey = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const { challengeId: id, options } = await beginPasskeySignIn();
      const r = await completePasskeySignIn(id, await startAuthentication({ optionsJSON: options }));
      if ('status' in r && r.status === 'failed') setNotice({ text: 'We don’t recognise that passkey. Sign in with your password instead, then add it again under My sign-in.', error: true });
      else handle(r);
    } catch (e) {
      // closing the browser's passkey window is a change of mind, not an error
      if ((e as Error).name !== 'NotAllowedError') setNotice({ text: 'We couldn’t use a passkey just now. Please sign in with your password.', error: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout onSubmit={submit}>
      <h1>{challengeId ? 'One more step' : 'Sign in'}</h1>
      {notice && <Notice tone={notice.error ? 'error' : 'neutral'}>{notice.text}</Notice>}
      {!challengeId ? (
        <>
          <TextField label="Email address" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          <TextField label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </>
      ) : (
        <>
          <p className="muted">Enter the six-digit code from your authenticator app{methods.includes('backup_code') ? ', or one of your backup codes' : ''}.</p>
          <TextField label="Code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
          <CheckField label="Remember this device for 30 days" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
        </>
      )}
      <Actions>
        <Button type="submit" busy={busy} busyLabel="Checking…">{challengeId ? 'Continue' : 'Sign in'}</Button>
        {challengeId && <Button variant="quiet" onClick={() => { setChallengeId(null); setCode(''); setNotice(null); }}>Start again</Button>}
        {!challengeId && browserSupportsWebAuthn() && <Button variant="quiet" disabled={busy} onClick={withPasskey}>Use a passkey</Button>}
      </Actions>
      <p className="links"><Link to="/recover">Can’t sign in?</Link></p>
    </AuthLayout>
  );
}
